import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Lock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreditOAuthDemo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // login | authorizing | success

  useEffect(() => {
    // Auto-proceed after component mounts (simulates OAuth)
    const timer = setTimeout(() => {
      handleAuth();
    }, 2000); // Wait 2 seconds to simulate loading Experian page

    return () => clearTimeout(timer);
  }, []);

  const handleAuth = async () => {
    setStep('authorizing');
    
    // Simulate authorization delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setStep('success');
    
    // Complete the connection
    try {
      const res = await axios.post(
        `${API}/credit/connect/setup`,
        { username: 'demo_oauth', password: 'demo_oauth' },
        { headers: getAuthHeaders() }
      );
      
      if (res.data.success) {
        // Wait a moment to show success
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get return path and redirect
        const returnPath = sessionStorage.getItem('credit_oauth_return') || '/credit';
        sessionStorage.removeItem('credit_oauth_return');
        
        toast.success('Credit monitoring connected!');
        navigate(returnPath);
      } else {
        toast.error('Connection failed');
        navigate('/credit');
      }
    } catch (error) {
      console.error('OAuth completion error:', error);
      toast.error('Failed to complete connection');
      navigate('/credit');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Simulated Experian Branding */}
        <div className="bg-white rounded-t-lg p-6 border-b-4 border-[#00A0DC]">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#00A0DC]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Experian</h1>
              <p className="text-sm text-gray-600">Credit Monitoring Portal</p>
            </div>
          </div>
        </div>

        {/* Auth Steps */}
        <div className="bg-white p-8">
          {step === 'login' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00A0DC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#00A0DC] animate-pulse" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Secure Authentication
              </h2>
              <p className="text-gray-600 mb-6">
                Loading secure login page...
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-[#00A0DC] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#00A0DC] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#00A0DC] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}

          {step === 'authorizing' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00A0DC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#00A0DC] animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Authorizing Access
              </h2>
              <p className="text-gray-600 mb-6">
                Securely connecting to your credit profile...
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Identity verified
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Permissions granted
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <div className="w-4 h-4 border-2 border-[#00A0DC] border-t-transparent rounded-full animate-spin" />
                  Establishing secure connection...
                </div>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connection Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Redirecting you back to Lee...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 rounded-b-lg p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <Lock className="w-3 h-3" />
            <span>Secured by 256-bit encryption</span>
          </div>
          {step === 'login' && (
            <button
              onClick={() => navigate('/credit')}
              className="mt-3 text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Cancel and return to Lee
            </button>
          )}
        </div>

        {/* Demo Notice */}
        <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-xs text-yellow-200 text-center">
            🔧 Demo Mode: This simulates the OAuth flow. In production, you'd see the real Experian login page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditOAuthDemo;
